import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { fmtINR, fmtNum, todayISO } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import { ChevronLeft, Download } from "lucide-react";

const KPI_LABELS = {
  hsd_litres: "HSD Litres",
  ms_litres: "MS Litres",
  cng_kg: "CNG Kg",
  sales_fuels_rs: "Fuels Sales (₹)",
  sales_cng_rs: "CNG Sales (₹)",
  lubricants_total: "Lubricants (₹)",
  digital_total: "Digital Payments (₹)",
  debtor_total: "Debtor Credit (₹)",
  flag_impact: "Flag Exposure (₹)",
};

export default function DrillThrough() {
  const { kpi } = useParams();
  const [sp] = useSearchParams();
  const [from, setFrom] = useState(sp.get("from") || todayISO());
  const [to, setTo] = useState(sp.get("to") || todayISO());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/reports/drill/${kpi}`, { params: { date_from: from, date_to: to } });
      setData(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => {
    load();
    // `load` is a stable in-component closure defined above; adding it to
    // the deps would require useCallback wrapping, which is scope for the
    // future refactor iteration (see PRD backlog · code-debt sweep).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kpi, from, to]);

  const isMoney = ["sales_fuels_rs", "sales_cng_rs", "lubricants_total", "digital_total", "debtor_total", "flag_impact"].includes(kpi);
  const isLitres = ["hsd_litres", "ms_litres", "cng_kg"].includes(kpi);

  const exportCSV = () => {
    if (!data?.rows?.length) return;
    const cols = Object.keys(data.rows[0]);
    const csv = [cols.join(",")].concat(
      data.rows.map((r) => cols.map((c) => JSON.stringify(r[c] ?? "")).join(","))
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${kpi}_${from}_${to}.csv`; a.click();
  };

  return (
    <div className="space-y-4" data-testid="drill-root">
      <Toaster position="top-center" />
      <div className="flex items-center gap-3">
        <Link to="/" className="text-slate-700 hover:text-slate-900 flex items-center gap-1 text-sm"><ChevronLeft size={16} /> Back to Dashboard</Link>
      </div>
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{KPI_LABELS[kpi] || kpi}</h1>
        <p className="text-sm text-slate-600 mt-1">Every entry that contributes to this number — click any row to open the source shift.</p>
      </div>

      <Card className="p-3 border-2 border-slate-300 rounded-sm flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-slate-600">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="block h-11 border border-slate-300 rounded-sm px-3 bg-white" data-testid="drill-from" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-slate-600">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="block h-11 border border-slate-300 rounded-sm px-3 bg-white" data-testid="drill-to" />
        </div>
        <div className="ml-auto flex items-center gap-3">
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Total</div>
            <div className="text-2xl num font-bold text-slate-900" data-testid="drill-total">
              {isMoney ? fmtINR(data?.total || 0) : fmtNum(data?.total || 0, 2)}
            </div>
            <div className="text-xs text-slate-500">{data?.count ?? 0} rows</div>
          </div>
          <Button data-testid="drill-export-csv" onClick={exportCSV} variant="outline" size="sm" className="border-slate-300"><Download size={12} className="mr-1" /> CSV</Button>
        </div>
      </Card>

      <Card className="p-0 border-2 border-slate-300 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="dense-table w-full text-sm">
            <thead className="bg-slate-100 sticky top-0">
              <tr>
                <th>Date</th><th>Shift</th>
                <th>Cashier / Attendee</th>
                {kpi === "flag_impact" ? <>
                  <th>Type</th><th>Severity</th><th>Actor</th><th>Message</th>
                </> : isLitres ? <>
                  <th>Nozzle</th><th>Opening</th><th>Closing</th><th>Litres/Kg</th><th>₹</th>
                </> : kpi === "lubricants_total" ? <>
                  <th>Product</th><th>Qty</th><th>₹</th>
                </> : kpi === "digital_total" ? <>
                  <th>Mode</th><th>Category</th><th>₹</th>
                </> : kpi === "debtor_total" ? <>
                  <th>Debtor</th><th>Fuel</th><th>Vehicle</th><th>Qty</th><th>₹</th>
                </> : <>
                  <th>₹</th>
                </>}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {busy && <tr><td colSpan={10} className="p-4 text-center text-slate-500">Loading…</td></tr>}
              {!busy && !data?.rows?.length && <tr><td colSpan={10} className="p-4 text-center text-slate-500">No data in this range.</td></tr>}
              {data?.rows?.map((r, i) => (
                <tr key={i} className="hover:bg-slate-50" data-testid={`drill-row-${i}`}>
                  <td>{r.date}</td>
                  <td>{r.shift_code}</td>
                  <td className="truncate max-w-[220px]">{r.cashier || r.attendees || r.actor_ref || "—"}</td>
                  {kpi === "flag_impact" ? <>
                    <td className="font-bold">{r.type}</td>
                    <td><span className={"px-1.5 py-0.5 text-[10px] font-bold rounded-sm " + (r.severity === "HIGH" ? "bg-red-100 text-red-800 border border-red-300" : "bg-amber-100 text-amber-800 border border-amber-300")}>{r.severity}</span></td>
                    <td>{r.actor_ref}</td>
                    <td className="text-xs">{r.message}</td>
                  </> : isLitres ? <>
                    <td>{r.nozzle_id}</td>
                    <td className="num">{fmtNum(r.opening, 2)}</td>
                    <td className="num">{fmtNum(r.closing, 2)}</td>
                    <td className="num font-bold">{fmtNum(r.net_litres, 2)}</td>
                    <td className="num">{fmtINR(r.net_rs)}</td>
                  </> : kpi === "lubricants_total" ? <>
                    <td>{r.lub_id}</td>
                    <td className="num">{r.qty}</td>
                    <td className="num font-bold">{fmtINR(r.amount)}</td>
                  </> : kpi === "digital_total" ? <>
                    <td>{r.pm_id}</td>
                    <td>{r.category}</td>
                    <td className="num font-bold">{fmtINR(r.amount)}</td>
                  </> : kpi === "debtor_total" ? <>
                    <td>{r.debtor_id}</td>
                    <td>{r.fuel}</td>
                    <td className="text-xs">{r.vehicle_no || "—"}</td>
                    <td className="num">{r.qty}</td>
                    <td className="num font-bold">{fmtINR(r.amount)}</td>
                  </> : <>
                    <td className="num font-bold">{fmtINR(r.amount)}</td>
                  </>}
                  <td>{r.entry_id && <Link to={`/shifts/${r.entry_id}`} className="text-xs text-slate-700 underline hover:text-slate-900">Open →</Link>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
